/**
 * The machinery behind every coalesced media resolve: one request per group per moment, retried as
 * a whole.
 *
 * Tiles ask for one key at a time. A batcher collects what arrives within one short window, per
 * GROUP — whatever a single batch request must be scoped to — and sends it as one request of at
 * most `MAX_BATCH_REFS` refs. `robot-media-batch.ts` builds the two batchers on top of this: helmet
 * captures (one group per device) and alert snapshots (one group for the tenant).
 *
 * RETRIES LIVE HERE, per chunk, not per tile. When each tile retried its own resolve, a batch of
 * fifty that failed came back as fifty separately jittered retries — fifty requests per round, sent
 * to a backend that was failing because it was overloaded. A chunk now retries as the chunk it was:
 * one request per round, and each waiter settles exactly once, with the final outcome.
 */

import { ApiRequestError } from '../../../services/api/types'

/** Long enough to catch one render's worth of tiles, short enough not to be felt. */
const FLUSH_DELAY_MS = 15

/** Mirrors the server's cap, shared by both batch routes. A bigger group goes out as several requests. */
export const MAX_BATCH_REFS = 100

/** First attempt plus retries, for a transient failure only. */
export const MAX_RESOLVE_ATTEMPTS = 4

const RETRY_DELAYS_MS = [500, 1500, 4000] as const

/**
 * A transient failure is one the SAME request can succeed on a moment later.
 *
 * Anything that is not an `ApiRequestError` is treated as transient: the client wraps every real
 * failure in one, so an unknown error is a bug, and caching a bug as "this photo is dead" is the
 * exact failure the resolver used to have.
 */
export function isTransientResolveError(err: unknown): boolean {
  if (!(err instanceof ApiRequestError)) return true

  return err.status === 0 || err.status === 408 || err.status === 429 || err.status >= 500
}

/**
 * The route is absent rather than refusing us. API Gateway answers an unknown REST path with 403
 * "Missing Authentication Token", not 404 — the console API is a `RestApi`.
 *
 * This only works where the browser gets to READ that answer: on-prem, where the backend answers
 * every preflight and sends CORS headers on its 404. The cloud console calls its API cross-origin
 * with an Authorization header, so the browser sends a preflight first, and API Gateway answers a
 * preflight for a missing route with a 403 too. A preflight that is not 2xx fails the fetch, which
 * arrives here as a status-0 network error: transient, not missing. So on cloud a missing batch
 * route never falls back to singles; each chunk costs `MAX_RESOLVE_ATTEMPTS` requests per
 * `TRANSIENT_COOLDOWN_MS` until the route exists. That is why the cloud deploy keeps the backend
 * ahead of the console (`deploy.yml` runs `cdk:services:deploy` before `cdk:apps:deploy`).
 */
function isMissingRoute(err: unknown): boolean {
  if (!(err instanceof ApiRequestError)) return false

  return (
    err.status === 404 ||
    err.status === 405 ||
    (err.status === 403 && /missing authentication token/i.test(err.message))
  )
}

/** Jittered, so separate chunks (and separate consoles) that failed together do not retry in lockstep. */
function retryDelay(attempt: number): number {
  const base = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)] ?? RETRY_DELAYS_MS[0]

  return base * (0.75 + Math.random() * 0.5)
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/** The two requests a resolver offers. Both reject with the transport error. */
export interface ResolveRoute {
  /** One ref → its URL, or null when the server answered without one. */
  single(ref: string, group: string | undefined): Promise<string | null>
  /** Many refs of ONE group → the URLs the server signed. A ref its gate refused is simply absent. */
  batch(refs: string[], group: string): Promise<Record<string, string>>
}

export interface ResolveBatcher {
  /**
   * Resolve one ref, sharing a request with every other ref of the same group asked for in the same
   * moment. With no group it cannot be batched and goes out alone. Rejects with the FINAL transport
   * error once retries are spent, so the caller can tell transient from definitive.
   */
  request(ref: string, group?: string): Promise<string | null>
  /** Test-only: settle anything queued, stop retries and in-flight batches, forget a learned missing route. */
  reset(): void
}

interface Waiter {
  resolve(url: string | null): void
  reject(err: unknown): void
}

type Waiters = Map<string, Waiter[]>

function settle(waiters: Waiters, ref: string, outcome: { url: string | null } | { err: unknown }): void {
  for (const waiter of waiters.get(ref) ?? []) {
    if ('err' in outcome) waiter.reject(outcome.err)
    else waiter.resolve(outcome.url)
  }
}

export function createResolveBatcher(route: ResolveRoute): ResolveBatcher {
  const pending = new Map<string, { waiters: Waiters; timer: ReturnType<typeof setTimeout> }>()

  /**
   * Set once the batch route turned out not to exist, so a console deployed ahead of its backend
   * quietly keeps working on single resolves for the rest of the session — where that can be told
   * at all (see `isMissingRoute`).
   */
  let batchUnavailable = false

  /**
   * Bumped by `reset` so a retry already sleeping, or a batch still in flight, cannot act on the
   * next spec: no further request, and no missing route learned after the reset.
   */
  let generation = 0

  /** One request, repeated on a transient failure until it succeeds or the attempts run out. */
  async function withRetries<T>(send: () => Promise<T>): Promise<T> {
    const gen = generation

    for (let attempt = 0; ; attempt += 1) {
      try {
        return await send()
      } catch (err) {
        if (!isTransientResolveError(err) || attempt >= MAX_RESOLVE_ATTEMPTS - 1) throw err

        await sleep(retryDelay(attempt))
        if (gen !== generation) throw err
      }
    }
  }

  function resolveSingle(ref: string, group: string | undefined): Promise<string | null> {
    return withRetries(() => route.single(ref, group))
  }

  async function sendSingles(refs: string[], group: string, waiters: Waiters): Promise<void> {
    await Promise.all(
      refs.map(ref =>
        resolveSingle(ref, group).then(
          url => settle(waiters, ref, { url }),
          (err: unknown) => settle(waiters, ref, { err }),
        ),
      ),
    )
  }

  async function sendChunk(refs: string[], group: string, waiters: Waiters): Promise<void> {
    // A lone ref gains nothing from the batch route, and a preview or lightbox is always alone.
    if (refs.length === 1 || batchUnavailable) return sendSingles(refs, group, waiters)

    const gen = generation

    try {
      const urls = await withRetries(() => route.batch(refs, group))

      // A ref the server left out was refused by its gate — a definitive "no", not a failure.
      for (const ref of refs) settle(waiters, ref, { url: urls[ref] ?? null })
    } catch (err) {
      // Reset while this batch was out: settle its own waiters, but neither learn a missing route
      // for the next generation nor send singles into it.
      if (gen === generation && isMissingRoute(err)) {
        batchUnavailable = true

        return sendSingles(refs, group, waiters)
      }

      for (const ref of refs) settle(waiters, ref, { err })
    }
  }

  function flush(group: string): void {
    const queued = pending.get(group)

    if (!queued) return
    pending.delete(group)

    const refs = [...queued.waiters.keys()]

    for (let i = 0; i < refs.length; i += MAX_BATCH_REFS) {
      void sendChunk(refs.slice(i, i + MAX_BATCH_REFS), group, queued.waiters)
    }
  }

  return {
    request(ref, group) {
      if (group === undefined || batchUnavailable) return resolveSingle(ref, group)

      return new Promise<string | null>((resolve, reject) => {
        let queued = pending.get(group)

        if (!queued) {
          queued = { waiters: new Map(), timer: setTimeout(() => flush(group), FLUSH_DELAY_MS) }
          pending.set(group, queued)
        }

        const list = queued.waiters.get(ref) ?? []

        list.push({ resolve, reject })
        queued.waiters.set(ref, list)
      })
    },

    reset() {
      for (const queued of pending.values()) {
        clearTimeout(queued.timer)
        // Settled, never abandoned: a promise nobody resolves would hang the spec awaiting it.
        for (const ref of queued.waiters.keys()) settle(queued.waiters, ref, { url: null })
      }

      pending.clear()
      batchUnavailable = false
      generation += 1
    },
  }
}
